import type { EstimateAlgorithm, Floor, StoredReport } from '@wrp/shared';
import { isLabelGroundTruth } from '@wrp/shared';
import { APP_NAME } from '../config';
import type { Point } from '../format';

export interface MonitorEntry {
  report_id: string;
  received_at: string;
  device_id: string;
  ap_count: number;
  /** Set when the report was labeled, i.e. it is survey data rather than a positioning query. */
  label: string | null;
  estimate: Point | null;
  matched_aps: number | null;
  query_aps: number | null;
  /** Loaded from a file: the estimate is a snapshot and is never recomputed. */
  frozen: boolean;
  /** Settings the estimate was computed with; '' while it has not been computed. */
  key: string;
}

export interface MonitorState {
  /** Empty until the page initialises it with the default title. */
  title: string;
  titleEdited: boolean;
  running: boolean;
  startedAt: string | null;
  algorithm: EstimateAlgorithm;
  k: number;
  entries: Record<string, MonitorEntry>;
}

export const INITIAL_MONITOR_STATE: MonitorState = {
  title: '',
  titleEdited: false,
  running: false,
  startedAt: null,
  algorithm: 'wknn',
  k: 4,
  entries: {},
};

const pad = (n: number) => String(n).padStart(2, '0');

/** "WiFi-Footprint-YYYYMMDD-HHmmss" */
export function defaultTitle(date = new Date()): string {
  const day = `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}`;
  const time = `${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}`;
  return `${APP_NAME.replace(/\s+/g, '-')}-${day}-${time}`;
}

export function fileNameFor(title: string): string {
  // eslint-disable-next-line no-control-regex
  const clean = title.replace(/[\\/:*?"<>|\u0000-\u001f]/g, '-').trim().replace(/^\.+/, '').slice(0, 120);
  return `${clean || defaultTitle()}.json`;
}

export function entryFromReport(report: StoredReport): MonitorEntry {
  return {
    report_id: report.id,
    received_at: report.received_at,
    device_id: report.device_id,
    ap_count: report.aps.length,
    label: report.ground_truth && isLabelGroundTruth(report.ground_truth) ? report.ground_truth.label : null,
    estimate: null,
    matched_aps: null,
    query_aps: null,
    frozen: false,
    key: '',
  };
}

// ---------------------------------------------------------------------- file

const FILE_FORMAT = 'wifi-footprint-monitoring';
const FILE_VERSION = 1;

export interface MonitoringFile {
  format: typeof FILE_FORMAT;
  version: number;
  saved_at: string;
  title: string;
  started_at: string | null;
  floor: { id: string; name: string; width_m: number; height_m: number };
  algorithm: EstimateAlgorithm;
  k: number;
  reports: Array<Omit<MonitorEntry, 'frozen' | 'key'>>;
}

export function buildMonitoringFile(floor: Floor, state: MonitorState, entries: MonitorEntry[]): MonitoringFile {
  return {
    format: FILE_FORMAT,
    version: FILE_VERSION,
    saved_at: new Date().toISOString(),
    title: state.title,
    started_at: state.startedAt,
    floor: { id: floor.id, name: floor.name, width_m: floor.width_m, height_m: floor.height_m },
    algorithm: state.algorithm,
    k: state.k,
    reports: entries.map(({ frozen: _frozen, key: _key, ...rest }) => rest),
  };
}

function asPoint(value: unknown): Point | null {
  if (!value || typeof value !== 'object') return null;
  const { x, y } = value as Record<string, unknown>;
  return typeof x === 'number' && typeof y === 'number' && Number.isFinite(x) && Number.isFinite(y) ? { x, y } : null;
}

const asCount = (value: unknown): number | null => (typeof value === 'number' && Number.isFinite(value) ? value : null);

/** Parses and validates a saved monitoring session. Throws an Error with a readable message. */
export function parseMonitoringFile(text: string): {
  title: string;
  startedAt: string | null;
  floorName: string;
  floorId: string;
  algorithm: EstimateAlgorithm | null;
  k: number | null;
  entries: MonitorEntry[];
} {
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    throw new Error('Not a valid JSON file');
  }
  const file = data as Partial<MonitoringFile> | null;
  if (!file || file.format !== FILE_FORMAT) throw new Error('Not a WiFi Footprint monitoring file');
  if (typeof file.version !== 'number' || file.version > FILE_VERSION) throw new Error('Unsupported monitoring file version');
  if (!Array.isArray(file.reports)) throw new Error('The file has no reports');

  const entries: MonitorEntry[] = [];
  for (const raw of file.reports) {
    if (!raw || typeof raw.report_id !== 'string') continue;
    entries.push({
      report_id: raw.report_id,
      received_at: typeof raw.received_at === 'string' ? raw.received_at : '',
      device_id: typeof raw.device_id === 'string' ? raw.device_id : '',
      ap_count: asCount(raw.ap_count) ?? 0,
      label: typeof raw.label === 'string' ? raw.label : null,
      estimate: asPoint(raw.estimate),
      matched_aps: asCount(raw.matched_aps),
      query_aps: asCount(raw.query_aps),
      frozen: true,
      key: '',
    });
  }
  const algorithm = file.algorithm === 'wknn' || file.algorithm === 'knn' || file.algorithm === 'multilateration' ? file.algorithm : null;
  return {
    title: typeof file.title === 'string' ? file.title : '',
    startedAt: typeof file.started_at === 'string' ? file.started_at : null,
    floorId: file.floor?.id ?? '',
    floorName: file.floor?.name ?? '',
    algorithm,
    k: typeof file.k === 'number' && file.k >= 1 ? Math.round(file.k) : null,
    entries,
  };
}
